import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button, Grid, Column, Tile, Tag, Accordion, AccordionItem, Checkbox } from '@carbon/react';
import {
  Temperature,
  Humidity,
  BatteryFull,
  WatsonHealthAiResults,
  ChartLine,
  Notification,
  DataBase,
  CloudMonitoring,
  Security,
  MobileCheck,
  Wifi,
  Time,
  LocationCompany,
  Report,
  ArrowRight,
  PhoneVoice,
  Email,
  Car,
  Building,
  ShoppingCart,
  Settings,
  Dashboard,
  Map,
  Play,
  Pause,
  DocumentExport,
  CheckmarkFilled,
  WarningAltFilled,
  QrCode,
  Bluetooth,
  Location,
  Analytics,
  Locked,
  Flash,
  Application,
  User,
  ShoppingBag,
  Gateway,
  Devices,
  DirectionStraight,
  Calendar,
  Download,
} from '@carbon/icons-react';
import { useTheme } from '../context/ThemeContext';

const FeaturesPage = () => {
  const navigate = useNavigate();
  const { theme } = useTheme();
  const [activeTab, setActiveTab] = useState(0);

  const isDark = theme === 'dark';
  const bgPrimary = isDark ? '#161616' : '#ffffff';
  const bgSecondary = isDark ? '#262626' : '#f4f4f4';
  const textPrimary = isDark ? '#f4f4f4' : '#161616';
  const textSecondary = isDark ? '#c6c6c6' : '#525252';
  const borderColor = isDark ? '#393939' : '#e0e0e0';

  const notifications = [
    { icon: Notification, title: 'Push Notifications', desc: 'Instant alerts on all your devices when thresholds are exceeded' },
    { icon: PhoneVoice, title: 'SMS Text Alerts', desc: 'Critical alerts via SMS directly to your phone' },
    { icon: Email, title: 'Email Notifications', desc: 'Detailed email alerts with sensor data and insights' },
  ];

  const mainFeatures = [
    {
      icon: Temperature,
      title: 'Temperature Monitoring',
      description: 'Track temperature in real-time across all your assets and locations',
      details: ['High-precision sensors', 'Custom threshold alerts', 'Historical data analysis'],
    },
    {
      icon: Humidity,
      title: 'Humidity Tracking',
      description: 'Monitor humidity levels to ensure optimal storage conditions',
      details: ['0-100% range', 'Dew point calculations', 'Trend analysis'],
    },
    {
      icon: ChartLine,
      title: 'Live Visualization',
      description: 'Interactive charts and dashboards for real-time monitoring',
      details: ['Real-time updates', 'Custom date ranges', 'Export capabilities'],
    },
    {
      icon: WatsonHealthAiResults,
      title: 'AI-Powered Insights',
      description: 'Get intelligent recommendations and predictive analytics',
      details: ['Anomaly detection', 'Predictive alerts', 'Optimization suggestions'],
    },
    {
      icon: Notification,
      title: 'Smart Alerts',
      description: 'Receive instant notifications when thresholds are breached',
      details: ['Push notifications', 'SMS alerts', 'Email reports'],
    },
    {
      icon: LocationCompany,
      title: 'Zone Management',
      description: 'Organize sensors by zones for better management',
      details: ['Custom zones', 'Zone-level reporting', 'Access controls'],
    },
  ];

  const faqs = [
    { question: 'How do I add a new sensor?', answer: 'Simply navigate to the Sensors page, click "Add Sensor", and follow the guided setup process. Most sensors can be added in under 60 seconds.' },
    { question: 'What happens if my sensor loses connection?', answer: 'Our sensors continue to log data offline and automatically sync when connection is restored. No data is ever lost.' },
    { question: 'Can I set different alert rules for different sensors?', answer: 'Yes! You can create custom alert rules for each sensor or group of sensors with different thresholds and notification preferences.' },
    { question: 'How long is historical data stored?', answer: 'We store all historical data indefinitely on our cloud platform. You can access and export data from any time period.' },
  ];

  // Zones Management Features
  const zoneTypes = {
    static: [
      { name: 'Office', icon: Building },
      { name: 'Room', icon: Building },
      { name: 'Cold Room', icon: Building },
      { name: 'Refrigerator', icon: Building },
      { name: 'Other', icon: Building },
    ],
    mobile: [
      { name: 'Car', icon: Car },
      { name: 'Truck', icon: Car },
      { name: 'Van', icon: Car },
    ],
  };

  // Multi-Device Sensor Support
  const sensorTypes = [
    {
      name: 'SIGMAWIT Sensors',
      models: ['T201', 'BLogm2'],
      tempRange: '-50°C to 100°C',
      humidityRange: '0% to 100%',
      battery: '1500-4000mV range',
      features: [
        'Historical data sync via Bluetooth',
        'On-device data logging',
        'Configurable broadcast intervals',
        'Low power consumption',
      ],
      icon: Devices,
    },
    {
      name: 'KBeacon Sensors',
      models: ['KBeacon Pro'],
      tempRange: '-40°C to 85°C',
      humidityRange: '0% to 100%',
      battery: '2000-3300mV range',
      features: [
        'iBeacon/Eddystone compatible',
        'Button-triggered events',
        'Motion detection',
        'Long battery life (1-2 years)',
      ],
      icon: Bluetooth,
    },
    {
      name: 'ELA Tag Sensors',
      models: ['ELA P-T-EN'],
      tempRange: '-20°C to 60°C',
      humidityRange: '0% to 100%',
      battery: 'Dual format (percentage or voltage 2800-3200mV)',
      features: [
        'Compact form factor',
        'Low battery alerts',
        'Real-time BLE advertising',
        'Cost-effective solution',
      ],
      icon: QrCode,
    },
  ];

  // Trip Tracking Features
  const tripFeatures = [
    { icon: Location, title: 'Real-time GPS Tracking', desc: 'Track latitude, longitude, and altitude' },
    { icon: Temperature, title: 'Continuous Monitoring', desc: 'Temperature and humidity throughout trip' },
    { icon: DirectionStraight, title: 'Route Visualization', desc: 'View complete route on interactive map' },
    { icon: ChartLine, title: 'Trip Statistics', desc: 'Distance, duration, speed, and environmental data' },
    { icon: Play, title: 'Trip Playback', desc: 'Replay entire trip with adjustable speed' },
  ];

  // Use Cases
  const useCases = [
    {
      title: 'Supply Chain & Logistics',
      icon: Car,
      desc: 'Track temperature-sensitive shipments in refrigerated trucks, vans, and cargo containers',
      features: ['Fleet monitoring', 'Route optimization', 'Compliance tracking'],
    },
    {
      title: 'Cold Chain Management',
      icon: Building,
      desc: 'Monitor pharmaceutical storage, food transportation, and perishable goods',
      features: ['FDA compliance', 'Vaccine storage', 'Specimen tracking'],
    },
    {
      title: 'Facility Management',
      icon: Building,
      desc: 'Track environmental conditions across warehouses, offices, cold rooms, and refrigerated storage',
      features: ['Multi-zone monitoring', 'HVAC optimization', 'Energy savings'],
    },
    {
      title: 'Quality Assurance',
      icon: Security,
      desc: 'Maintain compliance with temperature and humidity regulations (FDA, WHO, ISO)',
      features: ['Compliance reporting', 'Audit trails', 'Documentation'],
    },
    {
      title: 'Fleet Monitoring',
      icon: Car,
      desc: 'Real-time tracking of mobile assets (delivery trucks, refrigerated vehicles)',
      features: ['GPS tracking', 'Environmental monitoring', 'Route history'],
    },
    { 
      title: 'Food Safety',
      icon: Security,
      desc: 'Ensure food safety compliance with continuous temperature and humidity monitoring for restaurants, kitchens, and food storage',
      features: ['HACCP compliance', 'Food storage monitoring', 'Temperature alerts'],
    },
  ];

  return (
    <div style={{ backgroundColor: bgPrimary, minHeight: '100vh' }} data-testid="features-page">
      <style>{`
        button[data-testid="features-cta"] {
          color: #ffffff !important;
        }
        button[data-testid="features-cta"]:hover,
        button[data-testid="features-cta"]:active,
        button[data-testid="features-cta"]:focus {
          color: #ffffff !important;
          background-color: #002d9c !important;
        }

        button[data-testid="cta-signup"]:hover,
        button[data-testid="cta-signup"]:active,
        button[data-testid="cta-signup"]:focus {
          color: #ffffff !important;
          border-color: #ffffff !important;
          background-color: rgba(255, 255, 255, 0.15) !important;
        }

        button[data-testid="cta-contact"]:hover,
        button[data-testid="cta-contact"]:active,
        button[data-testid="cta-contact"]:focus {
          color: #ffffff !important;
          background-color: #4f2196 !important;
        }
      `}</style>
      {/* Hero */}
      <section style={{ backgroundColor: '#0f62fe', padding: '80px 0' }}>
        <Grid>
          <Column lg={10} md={6} sm={4}>
            <div style={{ color: '#ffffff' }}>
              <Tag type="blue" style={{ marginBottom: '16px' }}>Features</Tag>
              <h1 style={{ fontSize: '3rem', fontWeight: 600, marginBottom: '24px' }}>
                Powerful Tools for Comprehensive IoT, GPS and Cold Chain Monitoring
              </h1>
              <p style={{ fontSize: '1.25rem', opacity: 0.9, marginBottom: '32px' }}>
                Everything you need to monitor, analyze, and optimize your environmental conditions
              </p>
                <Button 
                  kind="ghost" 
                  size="lg" 
                  onClick={() => navigate('/signup')} 
                  data-testid="features-cta"
                  style={{ color: '#ffffff' }}
                >
                  Start Free Trial <ArrowRight style={{ marginLeft: '8px' }} />
                </Button>
              </div>
          </Column>
        </Grid>
      </section>

      {/* About ChillVerify */}
      <section style={{ backgroundColor: bgPrimary, padding: '80px 0' }}>
        <Grid>
          <Column lg={16} md={8} sm={4}>
            <div style={{ textAlign: 'center', marginBottom: '48px' }}>
              <Tag type="blue">About ChillVerify</Tag>
              <h2 style={{ color: textPrimary, marginTop: '16px', fontSize: '2.5rem', fontWeight: 600 }}>
                What is ChillVerify?
              </h2>
              <p style={{ color: textSecondary, maxWidth: '800px', margin: '16px auto 0', fontSize: '1.125rem', lineHeight: 1.8 }}>
                ChillVerify is a comprehensive <strong>environmental monitoring and logistics tracking application</strong> that enables real-time monitoring of temperature, humidity, and environmental conditions across multiple locations using Bluetooth Low Energy (BLE) sensors.
              </p>
            </div>
          </Column>
        </Grid>
        <Grid>
          <Column lg={16} md={8} sm={4}>
            <div style={{ textAlign: 'center', marginBottom: '32px' }}>
              <h3 style={{ color: textPrimary, marginBottom: '24px', fontSize: '1.75rem', fontWeight: 600 }}>
                Primary Use Cases
              </h3>
            </div>
          </Column>
          {useCases.map((useCase, idx) => (
            <Column lg={5} md={4} sm={4} key={idx}>
              <Tile style={{ backgroundColor: bgSecondary, padding: '32px', height: '100%' }}>
                <div style={{ display: 'flex', alignItems: 'flex-start', gap: '16px', marginBottom: '16px' }}>
                  <div style={{ backgroundColor: '#0f62fe', borderRadius: '8px', width: '48px', height: '48px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                    <useCase.icon size={24} style={{ color: '#ffffff' }} />
                  </div>
                  <div style={{ flex: 1 }}>
                    <h3 style={{ color: textPrimary, marginBottom: '8px', fontSize: '1.25rem', fontWeight: 600 }}>
                      {useCase.title}
                    </h3>
                    <p style={{ color: textSecondary, fontSize: '0.875rem', marginBottom: '16px' }}>
                      {useCase.desc}
                    </p>
                    <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                      {useCase.features.map((feature, fIdx) => (
                        <Tag key={fIdx} type="blue" size="sm">{feature}</Tag>
                      ))}
                    </div>
                  </div>
                </div>
              </Tile>
            </Column>
          ))}
        </Grid>
      </section>

      {/* Core Features Tabs */}
      <section style={{ backgroundColor: bgPrimary, padding: '80px 0' }}>
        <Grid>
          <Column lg={16} md={8} sm={4}>
            <div style={{ textAlign: 'center', marginBottom: '48px' }}>
              <Tag type="purple">Core Features</Tag>
              <h2 style={{ color: textPrimary, marginTop: '16px', fontSize: '2.5rem', fontWeight: 600 }}>
                Everything You Need for Complete IoT Environmental Monitoring
              </h2>
            </div>
            
            {/* Custom Tabs - matching SensorsPage style */}
            <div style={{ 
              display: 'flex', 
              gap: '0', 
              borderBottom: `1px solid ${borderColor}`,
              marginBottom: '32px'
            }}>
              {['Zones', 'Sensors', 'Trip Tracking', 'Reports', 'Alerts', 'Gateway', 'Quick Onboarding'].map((tabName, idx) => (
                <button
                  key={idx}
                  onClick={() => setActiveTab(idx)}
                  data-testid={`tab-${tabName.toLowerCase().replace(/\s+/g, '-')}`}
                  style={{ 
                    padding: '12px 24px',
                    fontSize: '0.875rem',
                    fontWeight: activeTab === idx ? 600 : 400,
                    color: activeTab === idx ? textPrimary : textSecondary,
                    backgroundColor: 'transparent',
                    border: 'none',
                    borderBottom: activeTab === idx ? `3px solid #0f62fe` : `3px solid transparent`,
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    marginBottom: '-2px',
                    outline: 'none'
                  }}
                  onMouseEnter={(e) => {
                    if (activeTab !== idx) {
                      e.target.style.color = textPrimary;
                      e.target.style.backgroundColor = bgSecondary;
                    }
                  }}
                  onMouseLeave={(e) => {
                    if (activeTab !== idx) {
                      e.target.style.color = textSecondary;
                      e.target.style.backgroundColor = 'transparent';
                    }
                  }}
                >
                  {tabName}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div>
                {/* Zones Tab */}
              {activeTab === 0 && (
                  <Grid style={{ marginTop: '32px' }}>
                    <Column lg={16} md={8} sm={4}>
                      <Tile style={{ backgroundColor: bgSecondary, padding: '32px', marginBottom: '24px' }}>
                        <h3 style={{ color: textPrimary, marginBottom: '24px', fontSize: '1.5rem', fontWeight: 600 }}>
                          Zones Management
                        </h3>
                        <p style={{ color: textSecondary, marginBottom: '32px' }}>
                          Create and manage different types of monitoring zones for static locations and mobile vehicles.
                        </p>
                        
                        <div style={{ marginBottom: '32px' }}>
                          <h4 style={{ color: textPrimary, marginBottom: '16px', fontSize: '1.125rem', fontWeight: 600 }}>
                            Static Zones
                          </h4>
                          <div style={{ display: 'flex', gap: '12px', flexWrap: 'nowrap', overflowX: 'auto', paddingBottom: '8px' }}>
                            {zoneTypes.static.map((zone, idx) => (
                              <Tag key={idx} type="blue" style={{ padding: '6px 12px', whiteSpace: 'nowrap', flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: '6px', flexDirection: 'row', height: 'auto', lineHeight: '1' }}>
                                <zone.icon size={14} style={{ display: 'inline-block', verticalAlign: 'middle' }} />
                                <span style={{ display: 'inline-block', verticalAlign: 'middle' }}>{zone.name}</span>
                              </Tag>
                            ))}
                          </div>
                        </div>

                        <div>
                          <h4 style={{ color: textPrimary, marginBottom: '16px', fontSize: '1.125rem', fontWeight: 600 }}>
                            Mobile Zones
                          </h4>
                          <div style={{ display: 'flex', gap: '12px', flexWrap: 'nowrap', overflowX: 'auto', paddingBottom: '8px' }}>
                            {zoneTypes.mobile.map((zone, idx) => (
                              <Tag key={idx} type="green" style={{ padding: '6px 12px', whiteSpace: 'nowrap', flexShrink: 0, display: 'inline-flex', alignItems: 'center', gap: '6px', flexDirection: 'row', height: 'auto', lineHeight: '1' }}>
                                <zone.icon size={14} style={{ display: 'inline-block', verticalAlign: 'middle' }} />
                                <span style={{ display: 'inline-block', verticalAlign: 'middle' }}>{zone.name}</span>
                              </Tag>
                            ))}
                          </div>
                        </div>

                        <div style={{ marginTop: '32px', padding: '24px', backgroundColor: bgPrimary, borderRadius: '8px' }}>
                          <h4 style={{ color: textPrimary, marginBottom: '16px', fontSize: '1.125rem', fontWeight: 600 }}>
                            Zone Features
                          </h4>
                          <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
                            {['Assign multiple sensors', 'Custom names and colors', 'Real-time conditions', 'Zone averages', 'Visual status indicators', 'Trip history (mobile)', 'Map view'].map((feature, idx) => (
                              <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <CheckmarkFilled size={16} style={{ color: '#24a148', flexShrink: 0 }} />
                                <span style={{ color: textSecondary, fontSize: '0.875rem' }}>{feature}</span>
                              </div>
                            ))}
                          </div>
                        </div>
                      </Tile>
                    </Column>
                  </Grid>
              )}

                {/* Sensors Tab */}
              {activeTab === 1 && (
                  <Grid style={{ marginTop: '32px' }}>
                    {sensorTypes.map((sensor, idx) => (
                      <Column lg={5} md={4} sm={4} key={idx}>
                        <Tile style={{ backgroundColor: bgSecondary, padding: '32px', height: '100%' }}>
                          <div style={{ backgroundColor: '#0f62fe', borderRadius: '50%', width: '56px', height: '56px', display: 'flex', alignItems: 'center', justifyContent: 'center', marginBottom: '20px' }}>
                            <sensor.icon size={28} style={{ color: '#ffffff' }} />
                          </div>
                          <h3 style={{ color: textPrimary, marginBottom: '12px', fontSize: '1.25rem', fontWeight: 600 }}>
                            {sensor.name}
                          </h3>
                          <div style={{ marginBottom: '16px' }}>
                            <p style={{ color: textSecondary, fontSize: '0.875rem', marginBottom: '4px' }}>
                              <strong>Models:</strong> {sensor.models.join(', ')}
                            </p>
                            <p style={{ color: textSecondary, fontSize: '0.875rem', marginBottom: '4px' }}>
                              <strong>Temp:</strong> {sensor.tempRange}
                            </p>
                            <p style={{ color: textSecondary, fontSize: '0.875rem', marginBottom: '4px' }}>
                              <strong>Humidity:</strong> {sensor.humidityRange}
                            </p>
                            <p style={{ color: textSecondary, fontSize: '0.875rem' }}>
                              <strong>Battery:</strong> {sensor.battery}
                            </p>
                          </div>
                          <div>
                            <h4 style={{ color: textPrimary, marginBottom: '12px', fontSize: '1rem', fontWeight: 600 }}>
                              Features:
                            </h4>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                              {sensor.features.map((feature, fIdx) => (
                                <div key={fIdx} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <CheckmarkFilled size={16} style={{ color: '#24a148', flexShrink: 0 }} />
                                  <span style={{ color: textSecondary, fontSize: '0.875rem' }}>{feature}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </Tile>
                      </Column>
                    ))}
                    <Column lg={16} md={8} sm={4}>
                      <Tile style={{ backgroundColor: bgSecondary, padding: '32px', marginTop: '24px' }}>
                        <h4 style={{ color: textPrimary, marginBottom: '16px', fontSize: '1.125rem', fontWeight: 600 }}>
                          Sensor Management Features
                        </h4>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
                          {[
                            'Automatic BLE discovery',
                            'QR code scanning',
                            'NFC pairing support (coming soon)',
                            'Manual device ID entry',
                            'Device-specific battery calculations',
                            'Last reading timestamps',
                            'Historical data sync',
                            'Multi-device concurrent monitoring',
                          ].map((feature, idx) => (
                            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <CheckmarkFilled size={16} style={{ color: '#24a148', flexShrink: 0 }} />
                              <span style={{ color: textSecondary, fontSize: '0.875rem' }}>{feature}</span>
                            </div>
                          ))}
                        </div>
                      </Tile>
                    </Column>
                  </Grid>
              )}

                {/* Trip Tracking Tab */}
              {activeTab === 2 && (
                  <Grid style={{ marginTop: '32px' }}>
                    <Column lg={16} md={8} sm={4}>
                      <Tile style={{ backgroundColor: bgSecondary, padding: '32px', marginBottom: '24px' }}>
                        <h3 style={{ color: textPrimary, marginBottom: '24px', fontSize: '1.5rem', fontWeight: 600 }}>
                          Trip Tracking for Mobile Zones
                        </h3>
                        <p style={{ color: textSecondary, marginBottom: '32px' }}>
                          Track complete journeys with environmental data for cars, trucks, and vans.
                        </p>
                        
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '24px', marginBottom: '32px' }}>
                          {tripFeatures.map((feature, idx) => (
                            <div key={idx} style={{ display: 'flex', alignItems: 'flex-start', gap: '16px' }}>
                              <div style={{ backgroundColor: '#6929c4', borderRadius: '8px', width: '40px', height: '40px', display: 'flex', alignItems: 'center', justifyContent: 'center', flexShrink: 0 }}>
                                <feature.icon size={20} style={{ color: '#ffffff' }} />
                              </div>
                              <div>
                                <h4 style={{ color: textPrimary, marginBottom: '8px', fontSize: '1rem', fontWeight: 600 }}>
                                  {feature.title}
                                </h4>
                                <p style={{ color: textSecondary, fontSize: '0.875rem' }}>{feature.desc}</p>
                              </div>
                            </div>
                          ))}
                        </div>

                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px' }}>
                          <div style={{ padding: '24px', backgroundColor: bgPrimary, borderRadius: '8px' }}>
                            <h4 style={{ color: textPrimary, marginBottom: '16px', fontSize: '1.125rem', fontWeight: 600 }}>
                              During Trip
                            </h4>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                              {['Real-time GPS tracking', 'Continuous temperature monitoring', 'Continuous humidity monitoring', 'Speed and heading tracking', 'Route visualization', 'Live sensor battery monitoring'].map((item, idx) => (
                                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <CheckmarkFilled size={16} style={{ color: '#24a148', flexShrink: 0 }} />
                                  <span style={{ color: textSecondary, fontSize: '0.875rem' }}>{item}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div style={{ padding: '24px', backgroundColor: bgPrimary, borderRadius: '8px' }}>
                            <h4 style={{ color: textPrimary, marginBottom: '16px', fontSize: '1.125rem', fontWeight: 600 }}>
                              After Trip
                            </h4>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                              {['Trip playback on map', 'Statistics (distance, duration, speed)', 'Temperature/humidity charts', 'Battery indicator', 'Trip history', 'Export trip data'].map((item, idx) => (
                                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <CheckmarkFilled size={16} style={{ color: '#24a148', flexShrink: 0 }} />
                                  <span style={{ color: textSecondary, fontSize: '0.875rem' }}>{item}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </Tile>
                    </Column>
                  </Grid>
              )}

                {/* Reports Tab */}
              {activeTab === 3 && (
                  <Grid style={{ marginTop: '32px' }}>
                    <Column lg={16} md={8} sm={4}>
                      <Tile style={{ backgroundColor: bgSecondary, padding: '32px' }}>
                        <h3 style={{ color: textPrimary, marginBottom: '24px', fontSize: '1.5rem', fontWeight: 600 }}>
                          Zone Reports & Analytics
                        </h3>
                        <p style={{ color: textSecondary, marginBottom: '32px' }}>
                          Comprehensive reporting with time range options and export capabilities.
                        </p>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px' }}>
                          <div style={{ padding: '24px', backgroundColor: bgPrimary, borderRadius: '8px' }}>
                            <h4 style={{ color: textPrimary, marginBottom: '16px', fontSize: '1.125rem', fontWeight: 600 }}>
                              Static Zone Reports
                            </h4>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                              {['High/Low temperature', 'High/Low humidity', 'Average temperature', 'Average humidity', 'Total sensor readings', 'Data coverage percentage', 'Daily breakdown'].map((item, idx) => (
                                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <CheckmarkFilled size={16} style={{ color: '#24a148', flexShrink: 0 }} />
                                  <span style={{ color: textSecondary, fontSize: '0.875rem' }}>{item}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div style={{ padding: '24px', backgroundColor: bgPrimary, borderRadius: '8px' }}>
                            <h4 style={{ color: textPrimary, marginBottom: '16px', fontSize: '1.125rem', fontWeight: 600 }}>
                              Mobile Zone Reports
                            </h4>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                              {['Total trips count', 'Total distance covered', 'Total duration', 'Average temperature', 'Average humidity', 'Individual trip cards', 'Trip playback access'].map((item, idx) => (
                                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <CheckmarkFilled size={16} style={{ color: '#24a148', flexShrink: 0 }} />
                                  <span style={{ color: textSecondary, fontSize: '0.875rem' }}>{item}</span>
                                </div>
                              ))}
                          </div>
                        </div>
                        </div>
                        <div style={{ marginTop: '24px', padding: '24px', backgroundColor: bgPrimary, borderRadius: '8px' }}>
                          <h4 style={{ color: textPrimary, marginBottom: '16px', fontSize: '1.125rem', fontWeight: 600 }}>
                            Export Options
                          </h4>
                          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap' }}>
                            {['CSV', 'PDF', 'Excel', 'API'].map((format, idx) => (
                              <Tag key={idx} type="cyan">
                                <DocumentExport size={16} style={{ marginRight: '8px', verticalAlign: 'middle' }} />
                                {format}
                              </Tag>
                            ))}
                          </div>
                        </div>
                      </Tile>
                    </Column>
                  </Grid>
              )}

                {/* Alerts Tab */}
              {activeTab === 4 && (
                  <Grid style={{ marginTop: '32px' }}>
                    <Column lg={16} md={8} sm={4}>
                      <Tile style={{ backgroundColor: bgSecondary, padding: '32px' }}>
                        <h3 style={{ color: textPrimary, marginBottom: '24px', fontSize: '1.5rem', fontWeight: 600 }}>
                          Alert Configuration
                            </h3>
                        <p style={{ color: textSecondary, marginBottom: '32px' }}>
                          Set up custom alert rules for temperature, humidity, battery, and connectivity.
                        </p>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '24px' }}>
                          <div style={{ padding: '24px', backgroundColor: bgPrimary, borderRadius: '8px' }}>
                            <h4 style={{ color: textPrimary, marginBottom: '16px', fontSize: '1.125rem', fontWeight: 600 }}>
                              Temperature Alerts
                            </h4>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                              {['High threshold', 'Low threshold', 'Range violations'].map((item, idx) => (
                                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <CheckmarkFilled size={16} style={{ color: '#24a148', flexShrink: 0 }} />
                                  <span style={{ color: textSecondary, fontSize: '0.875rem' }}>{item}</span>
                          </div>
                              ))}
                            </div>
                          </div>
                          <div style={{ padding: '24px', backgroundColor: bgPrimary, borderRadius: '8px' }}>
                            <h4 style={{ color: textPrimary, marginBottom: '16px', fontSize: '1.125rem', fontWeight: 600 }}>
                              Humidity Alerts
                            </h4>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                              {['High threshold', 'Low threshold', 'Range violations'].map((item, idx) => (
                                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <CheckmarkFilled size={16} style={{ color: '#24a148', flexShrink: 0 }} />
                                  <span style={{ color: textSecondary, fontSize: '0.875rem' }}>{item}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div style={{ padding: '24px', backgroundColor: bgPrimary, borderRadius: '8px' }}>
                        <h4 style={{ color: textPrimary, marginBottom: '16px', fontSize: '1.125rem', fontWeight: 600 }}>
                              Battery Alerts
                        </h4>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                              {['Low battery warnings', 'Critical battery alerts'].map((item, idx) => (
                            <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                              <CheckmarkFilled size={16} style={{ color: '#24a148', flexShrink: 0 }} />
                                  <span style={{ color: textSecondary, fontSize: '0.875rem' }}>{item}</span>
                            </div>
                          ))}
                            </div>
                          </div>
                          <div style={{ padding: '24px', backgroundColor: bgPrimary, borderRadius: '8px' }}>
                            <h4 style={{ color: textPrimary, marginBottom: '16px', fontSize: '1.125rem', fontWeight: 600 }}>
                              Connectivity Alerts
                            </h4>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                              {['Sensor offline', 'Connection lost'].map((item, idx) => (
                                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <CheckmarkFilled size={16} style={{ color: '#24a148', flexShrink: 0 }} />
                                  <span style={{ color: textSecondary, fontSize: '0.875rem' }}>{item}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </Tile>
                    </Column>
                  </Grid>
              )}

                {/* Gateway Tab */}
              {activeTab === 5 && (
                  <Grid style={{ marginTop: '32px' }}>
                    <Column lg={16} md={8} sm={4}>
                      <Tile style={{ backgroundColor: bgSecondary, padding: '32px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
                          <div style={{ backgroundColor: '#0f62fe', borderRadius: '50%', width: '64px', height: '64px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <Gateway size={32} style={{ color: '#ffffff' }} />
                          </div>
                          <div>
                            <h3 style={{ color: textPrimary, marginBottom: '8px', fontSize: '1.5rem', fontWeight: 600 }}>
                              Gateway Management
                            </h3>
                            <p style={{ color: textSecondary }}>
                              Extend Bluetooth range using ESP32-based gateways
                            </p>
                          </div>
                        </div>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(300px, 1fr))', gap: '24px' }}>
                          <div style={{ padding: '24px', backgroundColor: bgPrimary, borderRadius: '8px' }}>
                            <h4 style={{ color: textPrimary, marginBottom: '16px', fontSize: '1.125rem', fontWeight: 600 }}>
                              Extended Coverage
                            </h4>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                              {['Relay sensor data over WiFi/Cellular', 'Monitor beyond BLE range (10-30m)', 'Support multiple sensors per gateway', 'Automatic sensor discovery'].map((item, idx) => (
                                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <CheckmarkFilled size={16} style={{ color: '#24a148', flexShrink: 0 }} />
                                  <span style={{ color: textSecondary, fontSize: '0.875rem' }}>{item}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                          <div style={{ padding: '24px', backgroundColor: bgPrimary, borderRadius: '8px' }}>
                            <h4 style={{ color: textPrimary, marginBottom: '16px', fontSize: '1.125rem', fontWeight: 600 }}>
                              Gateway Configuration
                            </h4>
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                              {['WiFi setup and credentials', 'MQTT broker configuration', 'Gateway naming', 'Firmware updates OTA', 'Health monitoring'].map((item, idx) => (
                                <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                  <CheckmarkFilled size={16} style={{ color: '#24a148', flexShrink: 0 }} />
                                  <span style={{ color: textSecondary, fontSize: '0.875rem' }}>{item}</span>
                                </div>
                              ))}
                            </div>
                          </div>
                        </div>
                      </Tile>
                    </Column>
                  </Grid>
              )}

                {/* Quick Onboarding Tab */}
              {activeTab === 6 && (
                  <Grid style={{ marginTop: '32px' }}>
                    <Column lg={16} md={8} sm={4}>
                      <Tile style={{ backgroundColor: bgSecondary, padding: '32px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '16px', marginBottom: '24px' }}>
                          <div style={{ backgroundColor: '#6929c4', borderRadius: '50%', width: '64px', height: '64px', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                            <ShoppingCart size={32} style={{ color: '#ffffff' }} />
                          </div>
                          <div>
                            <h3 style={{ color: textPrimary, marginBottom: '8px', fontSize: '1.5rem', fontWeight: 600 }}>
                              Quick Onboarding
                            </h3>
                            <p style={{ color: textSecondary }}>
                              Buy and instantly onboard devices while we ship ready to use on arrival
                            </p>
                          </div>
                        </div>
                        <div style={{ padding: '24px', backgroundColor: bgPrimary, borderRadius: '8px', maxWidth: '600px' }}>
                            <h4 style={{ color: textPrimary, marginBottom: '16px', fontSize: '1.125rem', fontWeight: 600 }}>
                              Product Catalog
                            </h4>
                          <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                              {['SIGMAWIT sensors', 'KBeacon sensors', 'ELA Tag sensors', 'Gateway devices', 'Accessories'].map((item, idx) => (
                              <div key={idx} style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                                <CheckmarkFilled size={16} style={{ color: '#24a148', flexShrink: 0 }} />
                                <span style={{ color: textSecondary, fontSize: '0.875rem' }}>{item}</span>
                          </div>
                              ))}
                          </div>
                        </div>
                      </Tile>
                    </Column>
                  </Grid>
              )}
            </div>
          </Column>
        </Grid>
      </section>

      {/* FAQ */}
      <section style={{ backgroundColor: bgPrimary, padding: '80px 0' }} data-testid="faq-section">
        <Grid>
          <Column lg={8} md={4} sm={4}>
            <h2 style={{ color: textPrimary, marginBottom: '24px', fontSize: '2rem', fontWeight: 600 }}>
              Frequently Asked Questions
            </h2>
            <p style={{ color: textSecondary, marginBottom: '32px' }}>
              Get answers to common questions about our platform
            </p>
          </Column>
          <Column lg={8} md={4} sm={4}>
            <Accordion>
              {faqs.map((faq, index) => (
                <AccordionItem title={faq.question} key={index} data-testid={`faq-${index}`}>
                  <p style={{ color: textSecondary }}>{faq.answer}</p>
                </AccordionItem>
              ))}
            </Accordion>
          </Column>
        </Grid>
      </section>

      {/* CTA */}
      <section style={{ backgroundColor: '#6929c4', padding: '80px 0' }} data-testid="features-cta-section">
        <Grid>
          <Column lg={16} md={8} sm={4}>
            <div style={{ textAlign: 'center', color: '#ffffff' }}>
              <h2 style={{ fontSize: '2.5rem', fontWeight: 600, marginBottom: '16px' }}>
                Ready to Get Started?
              </h2>
              <p style={{ fontSize: '1.25rem', opacity: 0.9, marginBottom: '32px' }}>
                Join thousands of teams already using ChillVerify
              </p>
              <div style={{ display: 'flex', gap: '16px', justifyContent: 'center' }}>
                <Button 
                  kind="tertiary" 
                  size="lg" 
                  onClick={() => navigate('/signup')} 
                  data-testid="cta-signup"
                  style={{ color: '#ffffff', borderColor: '#ffffff', backgroundColor: 'transparent' }}
                >
                  Start Free Trial
                </Button>
                <Button 
                  kind="ghost" 
                  size="lg" 
                  onClick={() => navigate('/contact')} 
                  data-testid="cta-contact"
                  style={{ color: '#ffffff' }}
                >
                  Contact Sales
                </Button>
              </div>
            </div>
          </Column>
        </Grid>
      </section>
    </div>
  );
};

export default FeaturesPage;
